<template>
    <loading v-if="isLoading"></loading>
    <div v-else>
        <p>De planning bestaat uit {{ availableTerms.length }} periodes: <em>{{ termNames }}</em>. Elke periode is verdeeld in vier blokken met 1 tot 4 opdrachten per blok.</p>
        <p>U kunt de planning ook later nog aanpassen, zolang de periodes en blokken nog niet zijn begonnen.</p>

        <p><a href="#" class="tag" @click.prevent="isInfoVisible = !isInfoVisible">Hoe pas ik de planning aan?</a></p>

        <div v-if="isInfoVisible">
            <h3>Periode verplaatsen, blokken verlengen</h3>
            <p>Standaard duren de periodes 8 weken (2 weken voor elk blok, uitgaande van 100 lesminuten per week) en wordt er rekening gehouden met vakanties.</p>
            <p>U kunt een periode eerder of later in het jaar laten beginnen door de titel van de periode omhoog en omlaag te verslepen.</p>
            <p>Als u langer over een blok wilt doen (u wilt bijvoorbeeld tussendoor eigen opdrachten doen) kunt u het verlengen/verkorten door de pijltjes van het blok respectievelijk omlaag/omhoog te verslepen.</p>
            <p><img src="/build/img/planner.gif" alt="Planning aanpassen" width="296" style="width:296px; margin: 2em auto;"></p>
            <div class="note note--info">
                <p><strong>Let op:</strong> als een periode al is begonnen, kan deze niet meer versleept worden. U kunt nog wel de lengte van de blokken aanpassen, mits die blokken nog niet afgelopen zijn.</p>
                <p>Houd er ook rekening mee dat de laatste periode moet eindigen binnen 54 weken na het begin van de eerste periode. Zie de nummering aan de linkerkant.</p>
            </div>
            <h3>Periode verbergen</h3>
            <p>Er is ook de mogelijkheid om een hele periode te verbergen. Dit kunt u doen door op het oogje te klikken onderaan de periode. De leerlingen slaan de opdrachten van die periode dan over en kunnen met de volgende periode aan de slag.</p>
            <hr>
        </div>

        <week-planner v-model="plannerPeriods" :allowMovingSelectedPeriods="false" :relativeToPeriods="true" :minWeeksInPast="isAdmin ? 10 : 1" :maxWeeksInFuture="70" :classForWeek="classForWeek" :labelForWeek="labelForWeek" @clickedDate="chooseStartDate" :allowEditInPast="isAdmin" class="planner--kiempunt"></week-planner>

        <primary-button @clicked="submit" :loading="isSaving" v-transfer-dom="'modal__footer'">{{ isCreating ? 'Volgende' : 'Opslaan' }}</primary-button>
    </div>
</template>


<script>
import moment from 'js/helpers/moment'

import notify from 'js/components/notifications/notificationsMixin'
import validation from 'js/components/auth/validationMixin'
import holidaysMixin from 'js/components/groups/settings/holidaysMixin'

import WeekPlanner from 'js/components/groups/settings/WeekPlanner.vue'
import PrimaryButton from 'js/components/elements/PrimaryButton.vue'
import Loading from 'js/components/elements/Loading.vue'

import api from 'js/api/resources'
import { transform } from 'js/vuex/helpers'

export default {
    name: 'GroupSettingsPlanning',
    mixins: [ notify, validation, holidaysMixin ],
    components: { PrimaryButton, WeekPlanner, Loading },
    props: {
        group: {},
        school: {},
        isCreating: {
            type: Boolean,
            default: false
        },
        isDemo: {
            type: Boolean,
            default: false
        },
        isAdmin: {
            type: Boolean,
            default: false
        },
    },

    data: function() {
        return {
            plannerPeriods: [],
            holidays: [],
            weeksBetweenPeriods: 2,
            isSaving: false,
            isLoading: true,
            availableTerms: [],
            isInfoVisible: false,
        }
    },

    vuex: {
        actions: {
            updatePlanning: ({dispatch}, group, data) => {
                return api.patchPlanning(group.id, data)
                    .then( response => transform(response.body) )
                    .then( result => {
                        dispatch('ADD_TERMS', result.terms)
                        dispatch('ADD_PERIODS', result.periods)
                        dispatch('ADD_GROUP_TERMS', result.groupterms)
                        dispatch('ADD_GROUP_PERIODS', result.groupperiods)
                        dispatch('SET_GROUP_TERMS_FOR_GROUP', group.id, result.groupterms)
                    })
            },
        },
    },

    computed: {

        groupTerms: function() {
            return this.group.groupTerms.map( id => this.$store.state.groups.terms[id] )
        },

        termNames: function() {
            const titles = this.availableTerms.map( t => t.title)

            if ( titles.length < 2 ) {
                return titles[0]
            }

            return titles.slice(0, titles.length-1).join(', ') + ' en ' + titles[ titles.length - 1 ]
        },

    },

    methods: {

        termForGroupTerm: function(groupTerm) {
            return this.$store.state.terms.storage[ groupTerm.term[0] ]
        },

        classForWeek: function(date) {

            let classes = this.holidaysForDate(date)
                .map( h => 'holiday-' + h.title.toLowerCase() )

            if ( classes.length ) {
                classes.push('holiday')
            }

            return classes.map( c => 'planner__week--' + c)

        },

        labelForWeek: function(date) {
            return this.holidaysForDate(date)
                .map( h => h.title)[0]
        },

        chooseStartDate: function(date) {

            if (date.isSameOrAfter(moment()) && this.plannerPeriods.length && this.plannerPeriods[0].start == null ) {
                this.setInitialPeriods(date)
            }

        },

        defaultPlannerPeriod: function(term, start) {

            const blocks = term.periods.map( (p, i) => ({
                title: i + 1,
                duration: 2,
            }) )

            return {
                title: term.title,
                start: start ? start.clone() : moment().add(1,'week'),
                enabled: true,
                blocks,
            }

        },

        scheduleHolidays: function() {

            let previousPeriod = null
            for (const thisPeriod of this.plannerPeriods) {

                if ( previousPeriod != null ) {
                    const previousPeriodDuration = previousPeriod.blocks.reduce( (sum, b) => sum + b.duration, 0 )
                    const previousPeriodEnd = previousPeriod.start.clone().add( previousPeriodDuration, 'weeks')
                    const shiftedWeeksBetween = this.holidayShiftDuration( previousPeriodEnd, this.weeksBetweenPeriods)

                    thisPeriod.start = previousPeriodEnd.clone().add( shiftedWeeksBetween, 'weeks')
                }

                this.holidayShift(thisPeriod)
                previousPeriod = thisPeriod
            }

        },

        setPlannerPeriods: function() {

            let lastPeriodEnd = null
            // map over year terms, find in groupTerms, if not add default, disabled
            this.plannerPeriods = this.availableTerms.map( term => {

                const groupTerm = this.groupTerms.find( t => t.term[0] === term.id )
                if ( groupTerm == null ) {
                    const period = this.defaultPlannerPeriod(term, lastPeriodEnd)
                    period.enabled = false
                    lastPeriodEnd = period.start.clone()
                    return period
                }


                const blocks = groupTerm.groupPeriods
                    .map( id => this.$store.state.groups.periods[id] )
                    .map( gp => moment.unix(gp.end) )
                    .map( (thisEnd, i, endings) => {
                        const prevEnd = i === 0 ? moment.unix(groupTerm.start).subtract(1, 'days') : endings[i-1]
                        return {
                            title: i + 1,
                            duration: thisEnd.diff(prevEnd, 'weeks'),
                        }
                    })

                lastPeriodEnd = moment.unix(groupTerm.start).add( blocks.reduce( (sum, b) => sum + b.duration, 0), 'weeks')

                return {
                    title: this.termForGroupTerm(groupTerm).title,
                    start: moment.unix(groupTerm.start),
                    enabled: true,
                    blocks
                }
            })

        },

        submit: function(e) {
            e.preventDefault()
            this.save().catch(() => {})
        },

        save: function() {

            if ( this.isDemo ) {
                this.$demoSuccessNotification()
                this.$emit('saved')
                return Promise.resolve()
            }

            const data = this.plannerPeriods.map( (p, i) => ({
                term: this.availableTerms[i].id,
                start: p.start.format('Y-MM-DD'),
                periods: p.blocks.map( b => b.duration ),
                enabled: p.enabled,
            }) )

            this.isSaving = true
            const p = this.updatePlanning(this.group, data.filter( d => d.enabled ))
                .then( () => {
                    this.$emit('saved')
                    if ( !this.isCreating ) {
                        this.$success('De planning is aangepast')
                    }
                })
                .catch( e => {
                    this.$error('Aanpassen planning is mislukt')
                    return Promise.reject(e)
                })

            // finally
            p.catch( () => {} ).then( () => this.isSaving = false)
            return p

        },

        fetchAvailableTerms: function(levelId) {
            return api.getTermsForLevel( levelId )
                .then( response => transform(response.body) )
                .then( result => {
                    this.availableTerms = result.terms || []
                })
        }

    },



    created: function() {

        Promise.all([
            this.fetchAvailableTerms(this.group.level),
            this.fetchHolidays(this.school.id)
        ])
            .then( () => {

                this.isLoading = false
                this.setPlannerPeriods()

                this.$watch('plannerPeriods', () => this.$emit('changed'), {deep: true})

                // if this is the first (active, unstarted) group that is created for the current school
                // the given planning should automatically reschedule based on holidays
                const activeGroupSameLevel = this.school.groups
                    .map( id => this.$store.state.groups.storage[id] )
                    .filter(g => !g.archived && g.level == this.group.level && g.groupTerms.length)
                    .filter(g => {
                        const firstGT = this.$store.state.groups.terms[ g.groupTerms[0] ]
                        return firstGT.start > Math.floor(Date.now() / 1000)
                    })

                if ( this.isCreating && activeGroupSameLevel.length === 1) {
                    this.scheduleHolidays()
                }

            })

    }

}
</script>


<style lang="scss">

    @import 'global';

    .planner {

        @include modifier('kiempunt') {

            margin: 0 -3em;

            @include elements {

                &__week--holiday {

                    .planner__week-days-wrapper {

                        position: relative;

                        &:after {
                            content: attr(title);
                            display: block;
                            position: absolute;
                            top: 0;
                            left: 0;
                            z-index: 99;

                            pointer-events: none;

                            width: 100%;
                            height: $planner__row-height;
                            line-height: $planner__row-height;

                            text-align: center;
                            font-size: 0.8em;
                            font-weight: $font-body-bold-weight;

                            color: darken($color-grey, 20%);
                        }
                    }

                    @include elements {
                        &__day {
                            background-color: lighten($color-superlight-grey, 3%);
                        }
                    }


                }

                &__day {
                    border-left-color: $color-superlight-grey;
                    border-bottom-color: $color-superlight-grey;

                    &--saturday, &--sunday {
                        background-color: lighten($color-superlight-grey, 3%);
                    }

                    &--past {
                        background-color: $color-light-grey !important;
                        border-left-color: darken($color-light-grey, 3%);
                        border-bottom-color: darken($color-light-grey, 3%);
                    }

                    &--current {
                        background-color: $color-secondary !important;
                        border-left-color: darken($color-light-grey, 3%);
                    }

                    &:last-child {
                        border-right-color: $color-superlight-grey;
                    }

                }

                &__period {
                    @include gradient();
                    color: $color-typography-inverted;
                    width: 82px;
                    right: 0;
                    left: auto;
                    padding-left: 52px;
                    z-index: 100;

                    &.is-disabled {
                        z-index: 101;

                        &:before {
                            border-color: $color-grey;
                            width: 283px;
                            left: 0;
                        }

                        &:after {
                            border: none;
                        }

                    }

                    &.is-finalized {
                        &:before, &:after {
                          border-color: $color-grey;
                        }
                        @include elements {
                            &__period-title {
                                cursor: not-allowed;
                            }
                        }
                        .planner {
                          &__period-header {
                            background-color: $color-light-grey;
                            &:after {
                              @include sprite-position($icon-drag-ns-dark);
                              opacity: 0.3;
                            }
                          }
                          &__period-title {
                            color: $color-grey;
                          }
                          &__enabler {
                            display: none;
                          }
                        }
                    }

                    &:before, &:after {
                        width: 230px;
                    }

                    &:before {
                        border-color: $color-primary;
                    }

                    &:after {
                        border-color: $color-secondary;
                    }
                }

                &__period-header {
                    height: 100%;
                    width: 52px;
                    position: absolute;
                    left: 0;
                    overflow: hidden;
                    background-color: rgba(255,255,255,0.5);

                    &:after {
                        content: '';
                        @include background-sprite-replace($icon-drag-ns-primary-group);
                        position: absolute;
                        top: 18px;
                        left: 18px;
                    }

                    &:hover {
                        background-color: rgba(255,255,255,0.6);
                        &:after {
                            @include sprite-position($icon-drag-ns-dark);
                        }
                    }
                }

                &__period-title {
                    font-weight: $font-body-bold-weight;
                    color: $color-primary;
                    font-size: 0.8em;
                    text-transform: uppercase;

                    position: absolute;
                    z-index: 100;
                    cursor: move;
                    padding: 0 46px 0 9999px;
                    transform: translateX(-100%) rotate(-90deg);
                    transform-origin: right top;
                    left: 0;
                    top: 0;
                    line-height: 52px;
                    width: auto;
                    white-space: nowrap;

                    &:hover {
                        color: $color-typography;
                    }
                }



                &__enabler {
                    display: block;
                    position: absolute;
                    z-index: 100;
                    left: 0;
                    bottom: 0;

                    width: 52px;
                    padding-top: 40px;
                    overflow: hidden;
                    height: 0;

                    &.is-disabled {
                        background-color: $color-light-grey;
                        bottom: -20px;
                        left: -52px;

                        &:after {
                            @include sprite-position($icon-hide-primary);
                        }

                        &:hover {
                            &:after {
                                @include sprite-position($icon-show-dark);
                            }
                        }
                    }

                    &:after {
                        content: '';
                        @include background-sprite-replace($icon-show-primary-group);
                        position: absolute;
                        top: 12px;
                        left: 18px;
                    }

                    &:hover {
                        &:after {
                            @include sprite-position($icon-hide-dark);
                        }
                    }
                }

                &__block {
                    cursor: ns-resize;

                    &.is-finalized {
                        cursor: not-allowed;
                        background-color: $color-grey;
                        &:hover {
                          background-color: $color-grey;
                        }
                        .planner__extend-handle {
                          @include sprite-position($icon-drag-ns-dark);
                          opacity: 0.3;
                        }

                        @include elements {
                            &__extend-handle {
                                cursor: not-allowed;
                            }
                        }
                    }

                    &:hover {
                        background-color: rgba(255,255,255,0.2);
                        .planner__extend-handle {
                            @include sprite-position($icon-drag-ns-dark);
                        }
                    }

                }

                &__extend-handle {
                    @include background-sprite-replace($icon-drag-ns-group);
                    right: 6px;
                    bottom: 13px;
                }

            }

        }

    }


</style>



// WEBPACK FOOTER //
// Planning.vue?f0538bac